import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import './MiniTaskDialogue.css';

const ASSET_ROOT = '/mini-tasks/dialogue/v1';
const CHARACTERS = Object.freeze({
  a: ASSET_ROOT + '/student-boy-01.webp',
  b: ASSET_ROOT + '/student-girl-01.webp',
});
const SCENE_ALIASES = Object.freeze({
  // Generic / legacy 'school' scene uses the cleaner no-photo presentation.
  // Explicit 'classroom' and 'atrium' retain their artwork.
  school: 'minimal',
  classroom: 'classroom',
  atrium: 'atrium',
  minimal: 'minimal',
});

export function isMiniTaskDialogue(value) {
  const validTurns = Boolean(value && typeof value === 'object' && !Array.isArray(value)
    && Array.isArray(value.turns) && value.turns.length === 2
    && value.turns[0]?.speaker === 'a' && value.turns[1]?.speaker === 'b'
    && value.turns.every((turn) => typeof turn?.text === 'string' && Boolean(turn.text.trim()) && turn.text.length <= 400));
  if (!validTurns || (value.scene !== undefined
    && (typeof value.scene !== 'string' || !Object.prototype.hasOwnProperty.call(SCENE_ALIASES, value.scene)))) return false;
  const speakers = value.speakers === undefined ? {} : value.speakers;
  if (!speakers || typeof speakers !== 'object' || Array.isArray(speakers)) return false;
  return ['a', 'b'].every((side) => {
    const speaker = speakers[side] === undefined ? {} : speakers[side];
    if (!speaker || typeof speaker !== 'object' || Array.isArray(speaker)) return false;
    return speaker.name == null || (typeof speaker.name === 'string' && Boolean(speaker.name.trim()) && speaker.name.length <= 40);
  });
}

/**
 * Presentation only: deliberately unaware of the correct answer, feedback,
 * selection, timer, student progress or scoring.
 * Assets are independent WebP files; speech bubbles and text are real DOM.
 */
export default function MiniTaskDialogue({ dialogue, compact = false, scene: preferredScene, className = '' }) {
  if (!isMiniTaskDialogue(dialogue)) return null;
  const requestedScene = preferredScene || (compact ? 'minimal' : dialogue.scene) || 'school';
  const scene = SCENE_ALIASES[requestedScene] || 'minimal';

  return (
    <Box
      component="section"
      data-testid="mini-task-dialogue"
      aria-label="Student dialogue"
      style={scene === 'minimal' ? undefined : { backgroundImage: `url("${ASSET_ROOT}/scene-${scene}.webp")` }}
      className={['mini-task-dialogue', 'mini-task-dialogue--' + scene,
        compact && 'mini-task-dialogue--compact', className].filter(Boolean).join(' ')}
    >
      <Box className="mini-task-dialogue__people">
        {['a', 'b'].map((side, index) => (
          <Box key={side} className={'mini-task-dialogue__column mini-task-dialogue__column--' + side}>
            <Box className="mini-task-dialogue__bubble">
              <Typography component="p" className="mini-task-dialogue__bubble-text">
                {dialogue.turns[index].text}
              </Typography>
            </Box>
            <Box className="mini-task-dialogue__speaker">
              <Box className="mini-task-dialogue__student">
                <Box component="img" className="mini-task-dialogue__student-image"
                  src={CHARACTERS[side]} loading="eager" decoding="async" alt="" aria-hidden="true" />
              </Box>
              <Typography component="span" className="mini-task-dialogue__name">
                {dialogue.speakers?.[side]?.name || (side === 'a' ? 'Student A' : 'Student B')}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
